/**
 * Survey-wide countdown — all the decisions, none of the plumbing (spec 35).
 *
 * Pure functions over (state, now). No DOM, no network, no clock of their own, so every rule
 * here is testable without rendering anything or waiting for real time to pass.
 *
 * The design in one line: the timer is a progress-reassurance device that happens to have a
 * deadline. A clock shown too early converts reassurance into pressure, which costs both
 * completion rate and answer quality — the opposite of why a researcher turns it on.
 */

import { UI_TEXT } from '../../config/ui-text';

/**
 * Every respondent-facing line this module chooses between.
 *
 * The decisions live here; the words live in `ui-text.ts` with the rest of the product's copy.
 * They were inline until now — while the same PR routed its other new strings through that file
 * — so the four nudge lines, the welcome-back greeting and the readout's two labels were the
 * only respondent-facing text in the survey runtime that a copy edit could not find.
 */
const TIMER_TEXT = UI_TEXT.HUMAN_SURVEY.SESSION.TIMER;

export type SurveyTimerMode = 'guidance' | 'hard_stop';

export interface SurveyTimerConfig {
  enabled: boolean;
  duration_seconds: number;
  mode: SurveyTimerMode;
  visible: boolean;
}

/** The single snapshot the hook holds. Everything on screen is derived from it. */
export interface TimerSnapshot {
  /** Server's authoritative remaining seconds at the moment it was read. */
  serverRemainingSeconds: number;
  /**
   * `Date.now()` when that arrived, for interpolating between heartbeats.
   *
   * Wall-clock rather than a monotonic one, so a device clock correction mid-survey can skew the
   * interpolation. The next heartbeat overwrites the whole snapshot, which bounds any such skew
   * to one heartbeat interval — and the server, not this, decides when the survey ends.
   */
  readAtMs: number;
  paused: boolean;
}

/** Seconds-remaining marks at which the respondent is nudged, largest first. */
export const NUDGE_THRESHOLDS_SECONDS = [15 * 60, 10 * 60, 5 * 60, 60] as const;
/** At or below this, a persistent readout is shown as well as the transient nudges. */
export const PERSISTENT_READOUT_SECONDS = 10 * 60;
/** At or below this the readout becomes a real MM:SS countdown. */
export const FINAL_COUNTDOWN_SECONDS = 60;
/** At or below this a nudge is shown in its amber register rather than its calm one. */
export const NUDGE_URGENT_SECONDS = 5 * 60;
/** How long a nudge stays on screen before fading. */
export const NUDGE_VISIBLE_MS = 8000;
/** Heartbeat cadence — also the server's pause-detection channel. */
export const HEARTBEAT_INTERVAL_MS = 60_000;
/**
 * Consecutive failed timer heartbeats before the plain liveness ping is asked to take over.
 *
 * Two, not one: a single blip on a flaky connection is not worth a handover, and every handover
 * costs an interval of double-beating. Two is still well inside the abandonment sweep's window,
 * which is the thing being protected against.
 */
export const HEARTBEAT_FAILURES_BEFORE_FALLBACK = 2;
/**
 * The cadence this hook's own beat drops to once the liveness ping has taken over from it.
 *
 * "Exactly one of the two may run" was documented and then not held: the handover ARMS the
 * liveness ping without standing this hook's beat down — its effect is keyed on the session, not
 * on the channel's health — so a persistent failure left both POSTing the SAME endpoint every
 * sixty seconds, for the life of the page, on the unauthenticated respondent path.
 *
 * Stopping outright is not the alternative. This beat is the only thing that can discover the
 * channel is back (the liveness ping is a `sendBeacon` and cannot report anything), so ownership
 * would never return and the clock would interpolate uncorrected for the rest of the session. So
 * it becomes a PROBE: one request per five minutes instead of one per minute, which puts the
 * combined rate back within a fifth of the healthy one and still finds a recovery inside the
 * window a respondent would notice.
 */
export const HEARTBEAT_PROBE_INTERVAL_MS = 5 * 60_000;
/**
 * How long to wait before asking the server again to end a hard-stop session.
 *
 * A refusal means "not yet", not "never": the server's clock is the authority and ours may be a
 * second or two fast. Backing off rather than giving up is what stops one early ask from
 * silently cancelling the hard stop for the rest of the session; backing off rather than
 * retrying freely is what stops it becoming a request per second at zero.
 */
export const EXPIRY_RETRY_INTERVAL_MS = 30_000;
/**
 * The server's buzzer grace, mirrored from `SURVEY_TIMER_CONFIG['HARD_STOP_GRACE_SECONDS']`.
 *
 * Held here for exactly one purpose — timing the retry after a refusal — and never to decide
 * anything the server decides. An answer sent within this window of the deadline is still saved,
 * and the session only becomes eligible to be ended once past it.
 */
export const HARD_STOP_GRACE_SECONDS = 5;
/**
 * How long after a refusal to ask again, derived from the reading that refusal carried.
 *
 * The FIRST ask of every hard-stop session is refused by construction: the client asks the moment
 * its own interpolation hits zero, while the server grants only once the deadline is past by more
 * than the grace above — and the client's reading is if anything slightly BEHIND the server's,
 * since `readAtMs` is stamped when the request left. So the flat interval was never a backoff
 * from a real disagreement; it was a flat 30-second delay on EVERY hard stop, landing it ~30s
 * late instead of ~5s. Every one of those seconds is a window in which the backend accepts the
 * respondent's answers with `status: success` and writes nothing.
 *
 * Whatever the server says it still has left, plus the grace it will insist on, plus a second so
 * the ask lands just past the boundary rather than exactly on it. Bounded by the flat interval,
 * which stays the answer for a refusal that carried no usable reading at all — so this can only
 * ever make the retry sooner than it was, never later.
 */
export const expiryRetryDelayMs = (
  timer: { remaining_seconds?: unknown } | null | undefined,
): number => {
  const remaining = timer?.remaining_seconds;
  if (typeof remaining !== 'number' || !Number.isFinite(remaining) || remaining < 0) {
    return EXPIRY_RETRY_INTERVAL_MS;
  }
  return Math.min(EXPIRY_RETRY_INTERVAL_MS, (remaining + HARD_STOP_GRACE_SECONDS + 1) * 1000);
};
/**
 * How long the hard stop waits for the caller's last write before going ahead without it.
 *
 * That write is awaited so a live rapid-fire block can be recorded while the session still
 * accepts writes. Swallowing a rejection is not enough to keep it from blocking: no request in
 * `surveyApi` carries an `AbortController`, so one that STALLS never rejects at all — and the ask
 * behind it, plus every retry, waited on it for as long as the stall lasted while the respondent
 * carried on past the researcher's deadline.
 *
 * Ten seconds rather than one: a slow-but-alive close still lands, and what it records is lost
 * for good once the ask completes the session. Ten seconds of overrun on a survey that is already
 * at zero is the cheaper side of that trade.
 */
export const BEFORE_HARD_STOP_TIMEOUT_MS = 10_000;
/**
 * How long either of the timer's OWN requests may hang before it is asked again.
 *
 * The same hole as the one above, in the two places the hook does the asking rather than the
 * waiting. `startSurveyTimer` and `expireSurveyTimer` are plain `fetch` calls with no
 * `AbortController` between them, so the third outcome — neither a reply nor a rejection, just
 * silence — is the one every retry path was blind to: each latches a ref before the request
 * leaves, and only the settled outcomes ever give it back. A hung start left the survey with no
 * clock for the life of the page, and in hard-stop mode no deadline at EITHER end; a hung expire
 * left the buzzer unable to land at all.
 *
 * ONE number for both, because it answers one question — how long a request that is going to
 * arrive could honestly take — and there is no evidence for two. Fifteen seconds is far beyond a
 * healthy round trip on a bad panel connection, so a slow-but-alive request still lands and is
 * still taken; and it bounds the two failures above to fifteen seconds plus a retry each, rather
 * than to the rest of the session.
 */
export const REQUEST_STALL_TIMEOUT_MS = 15_000;
/** Anything shorter than this counts as "no interaction" for the idle report. */
export const IDLE_REPORT_FLOOR_MS = 1000;
/**
 * The shortest gap between two heartbeats a returning tab may cause.
 *
 * The visibility beat exists so the clock is right before the respondent reads it rather than up
 * to a minute stale. Unthrottled it is one POST per visibility change, on top of the 60-second
 * interval and on the unauthenticated respondent path — and someone flicking between tabs while
 * they think generates them as fast as they can switch. Ten seconds of staleness on a readout
 * that only ever shows a minute label is invisible; ten seconds is also six requests a minute at
 * the very worst, against the sixty a rapid switcher could otherwise produce.
 */
export const VISIBILITY_BEAT_MIN_INTERVAL_MS = 10_000;
/**
 * First wait before asking the server again to START the clock, and the ceiling that wait backs
 * off to.
 *
 * A failed start used to be permanent: the latch was taken before the request and never given
 * back, and /heartbeat carries no timer at all for a session the server never started one for. So
 * one dropped request left the survey with no clock for the rest of the session — and in
 * hard-stop mode with no deadline at EITHER end, since the server's own check reads the same
 * timer state that call would have created.
 *
 * Retrying is what makes the deadline real; the ceiling is what stops the retry becoming a
 * request storm. At its slowest this costs one request a minute — the rate of the heartbeat
 * already running beside it.
 */
export const START_RETRY_INTERVAL_MS = 5_000;
export const START_RETRY_MAX_INTERVAL_MS = HEARTBEAT_INTERVAL_MS;

/** The wait before the nth attempt to start the clock (n = 1 being the first retry), bounded. */
export const startRetryDelayMs = (attempt: number): number =>
  Math.min(START_RETRY_MAX_INTERVAL_MS, START_RETRY_INTERVAL_MS * 2 ** Math.max(0, attempt - 1));

export const timerConfigFrom = (survey: unknown): SurveyTimerConfig | null => {
  const cfg = (survey as { timer_config?: Partial<SurveyTimerConfig> } | null)?.timer_config;
  if (!cfg || !cfg.enabled) return null;
  const duration =
    typeof cfg.duration_seconds === 'number' && cfg.duration_seconds > 0 ? cfg.duration_seconds : 0;
  if (!duration) return null;
  return {
    enabled: true,
    duration_seconds: duration,
    mode: cfg.mode === 'hard_stop' ? 'hard_stop' : 'guidance',
    visible: cfg.visible !== false,
  };
};

/**
 * Which nudges this survey can actually use.
 *
 * Thresholds at or above the total duration are dropped rather than scaled: a 6-minute survey
 * gets the 5-minute and final-minute marks and nothing else. Scaling them proportionally was
 * considered and rejected — it produces copy like "about 3 minutes left" at 2m42s, which reads
 * as a stopwatch rather than reassurance, and the whole point is the opposite.
 */
export const applicableThresholds = (durationSeconds: number): number[] =>
  NUDGE_THRESHOLDS_SECONDS.filter((t) => t < durationSeconds);

/**
 * Remaining seconds right now, interpolated from the last server reading.
 *
 * DERIVED, never stored. A second ticking counter would be a second source of truth that drifts
 * from the server on every reload, pause and network hiccup — the exact class of bug this module
 * exists to avoid.
 */
export const remainingSeconds = (snapshot: TimerSnapshot | null, nowMs: number): number | null => {
  if (!snapshot) return null;
  // A paused clock is frozen: the server is not counting down, so neither do we.
  if (snapshot.paused) return snapshot.serverRemainingSeconds;
  const elapsed = Math.max(0, (nowMs - snapshot.readAtMs) / 1000);
  return Math.max(0, Math.round(snapshot.serverRemainingSeconds - elapsed));
};

/** What one tick does about the nudge marks: at most one to raise, and everything now behind us. */
export interface NudgeDecision {
  /** The mark to announce on this tick, or null. */
  raise: number | null;
  /** Every mark now passed — recorded whether or not it spoke, so none can fire late. */
  seen: number[];
}

/**
 * Which mark, if any, this tick should raise — decided from the PREVIOUS reading, not just the
 * current one.
 *
 * A mark speaks only when this tick actually watched it go by (`previous > mark >= remaining`).
 * Anything already behind us when we first looked is recorded as seen and never spoken: a reload
 * at two minutes left must not announce "about 15 minutes left", which is not merely stale but
 * false, and which the readout beside it contradicts.
 *
 * When several marks go by between two ticks — a throttled background tab, a heartbeat
 * correcting the clock sharply downward — only the most urgent one speaks. The outer ones have
 * been overtaken, and firing them in turn would count the respondent DOWN through several
 * contradictory messages, one per second.
 */
export const nudgeDecision = (
  previousRemaining: number | null,
  remaining: number | null,
  durationSeconds: number,
  alreadyShown: readonly number[],
): NudgeDecision => {
  if (remaining === null) return { raise: null, seen: [...alreadyShown] };

  // Descending, so the last element of any slice is the most urgent.
  const passed = applicableThresholds(durationSeconds).filter((t) => remaining <= t);
  const fresh = passed.filter((t) => !alreadyShown.includes(t));
  const watched = previousRemaining === null ? [] : fresh.filter((t) => previousRemaining > t);

  return {
    raise: watched.length ? watched[watched.length - 1] : null,
    seen: [...alreadyShown, ...fresh],
  };
};

export const nudgeCopyFor = (threshold: number): string => {
  switch (threshold) {
    case 15 * 60:
      return TIMER_TEXT.NUDGES.FIFTEEN_MINUTES;
    case 10 * 60:
      return TIMER_TEXT.NUDGES.TEN_MINUTES;
    case 5 * 60:
      return TIMER_TEXT.NUDGES.FIVE_MINUTES;
    case 60:
      return TIMER_TEXT.NUDGES.FINAL_MINUTE;
    default:
      return TIMER_TEXT.NUDGES.ABOUT_MINUTES(Math.round(threshold / 60));
  }
};

/**
 * Whether the persistent readout shows.
 *
 * Normally from the 10-minute mark. On a survey shorter than that, from its own largest nudge
 * instead — otherwise the readout would be pinned to the screen from the very first question,
 * which is the pressure the design is built to avoid.
 *
 * A survey at or under a minute has no applicable mark at all, and the fallback there is the
 * final minute, not the ten-minute default: defaulting to ten made the start LATER than the
 * survey is long, which pinned the readout from question one — the very outcome this rule
 * exists to prevent. Such a survey is entirely inside its final minute, so that is honest.
 */
export const shouldShowReadout = (
  remaining: number | null,
  durationSeconds: number,
  visible: boolean,
): boolean => {
  if (!visible || remaining === null) return false;
  const applicable = applicableThresholds(durationSeconds);
  const start = Math.min(PERSISTENT_READOUT_SECONDS, applicable[0] ?? FINAL_COUNTDOWN_SECONDS);
  return remaining <= start;
};

export const isFinalCountdown = (remaining: number | null): boolean =>
  remaining !== null && remaining <= FINAL_COUNTDOWN_SECONDS;

/**
 * A guidance clock with nothing left to count.
 *
 * Guidance deliberately never ends the survey, so `remaining` sits at 0 for the whole remainder —
 * which pinned a red "Final minute" over a frozen 0:00 to the screen of someone who was still
 * answering. That is a threat that will never be carried out, attached to a countdown that has
 * stopped counting. In hard-stop mode zero means what it says, so this is never true there.
 */
export const isSpentGuidanceClock = (remaining: number | null, mode: SurveyTimerMode): boolean =>
  mode === 'guidance' && remaining !== null && remaining <= 0;

/** The readout as the respondent sees it: whether it shows at all, and in which register. */
export interface ReadoutState {
  show: boolean;
  final: boolean;
}

export const readoutState = (remaining: number | null, config: SurveyTimerConfig): ReadoutState => {
  if (isSpentGuidanceClock(remaining, config.mode)) return { show: false, final: false };
  return {
    show: shouldShowReadout(remaining, config.duration_seconds, config.visible),
    final: isFinalCountdown(remaining),
  };
};

/** Whether a nudge is shown in its amber register. Subsumes the final minute. */
export const isNudgeUrgent = (remaining: number | null): boolean =>
  remaining !== null && remaining <= NUDGE_URGENT_SECONDS;

/**
 * The whole minutes a respondent can still count on, as the words that quote a number to them.
 *
 * ROUNDS DOWN, everywhere and in both modes. Rounding to nearest reads as the kinder choice, and
 * on a guidance clock it is — but it over-states the remaining time by up to 30 seconds, and on a
 * hard stop the readout is then promising time the respondent does not have, at the exact moment
 * they are deciding whether to keep typing. "2 minutes left" at 1:30 is not reassurance; it is the
 * reason their answer was still unfinished when the buzzer went.
 *
 * One rule rather than one per mode, deliberately. Two would make the same clock read differently
 * depending on a setting the respondent cannot see, and the reassurance argument does not survive
 * the fact that a guidance readout is ALSO used to plan how much more to write. Under-stating by
 * less than a minute is never a claim about time they do not have; over-stating always is.
 *
 * Floored at one, so the calm register never reads "0 minutes left" — below the final minute the
 * readout has switched to a real MM:SS countdown anyway.
 *
 * That floor is the whole of this function's precondition, and it is not self-enforcing: it is
 * only ever TRUE because the sole caller, `formatRemaining`, has already returned for anything at
 * or below FINAL_COUNTDOWN_SECONDS. Any other caller has to guard for itself — `welcomeBackMessage`
 * does, and it exists because the line that greets a returning respondent did not, and announced
 * "About 1 minute left" over a red 0:20.
 */
export const wholeMinutesLeft = (remaining: number): number =>
  Math.max(1, Math.floor(remaining / 60));

/** What the calm register calls a whole number of minutes: "About 7 minutes left". */
const aboutMinutesLeft = (remaining: number): string =>
  TIMER_TEXT.NUDGES.ABOUT_MINUTES(wholeMinutesLeft(remaining));

/**
 * The line a respondent is greeted with when the server resumes their paused clock.
 *
 * The other place this module quotes a figure to the respondent, and the one that lands while
 * they are deciding whether to carry on — so it obeys the same rule the readout does: never state
 * more time than they have.
 *
 * Below the final minute it stops quoting minutes altogether rather than rounding within them.
 * `wholeMinutesLeft` floors at 1, which is correct for `formatRemaining` — that guards on
 * FINAL_COUNTDOWN_SECONDS and switches to MM:SS, so the floor is never reached there — and a
 * straight over-statement here: resuming a hard stop with twenty seconds left announced "About 1
 * minute left" directly above a red 0:20.
 *
 * "Less than a minute left" rather than the digits themselves, deliberately. This mark is a fixed
 * string shown for NUDGE_VISIBLE_MS while the clock underneath it keeps draining, so "0:20 left"
 * is wrong three seconds after it appears, where this stays true for as long as it is on screen —
 * and the readout beside it is already showing the exact figure.
 */
export const welcomeBackMessage = (remaining: number): string =>
  TIMER_TEXT.WELCOME_BACK(
    remaining <= FINAL_COUNTDOWN_SECONDS
      ? TIMER_TEXT.WELCOME_BACK_UNDER_A_MINUTE
      : aboutMinutesLeft(remaining),
  );

/** "12 minutes left" above the final minute; "0:47" inside it. */
export const formatRemaining = (remaining: number | null): string => {
  if (remaining === null) return '';
  if (remaining <= FINAL_COUNTDOWN_SECONDS) {
    const m = Math.floor(remaining / 60);
    const s = remaining % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }
  return TIMER_TEXT.MINUTES_LEFT(wholeMinutesLeft(remaining));
};

/**
 * Everything the timer puts on screen right now, as one comparable string.
 *
 * The hook lives inside the respondent runtime, so every state update it makes re-renders a
 * ~4,900-line component. A once-a-second tick that changes nothing visible — most of a long
 * survey, all of a hidden clock, every second of the calm readout except the one where the
 * minute turns over — is pure cost, and this is how the tick knows to stay quiet.
 *
 * Deliberately about the SCREEN, not about the clock: `paused` is absent because the tick does
 * not run while paused, and the hard stop's own need to tick is a separate question the caller
 * asks separately.
 */
export const onScreenSignature = (
  remaining: number | null,
  config: SurveyTimerConfig,
  nudgeVisible: boolean,
): string => {
  const { show, final } = readoutState(remaining, config);
  // The register rides inside the readout rather than standing on its own: a tone nobody can see
  // is not a change, and a clock the researcher hid has no readout to tone.
  //
  // Inside the final minute the digits move every second and the signature has to move with them.
  // The calm register above it only ever shows a LABEL, and that label turns over once a minute —
  // so keeping per-second precision there re-rendered the ~4,900-line respondent runtime once a
  // second for the last ten minutes of every timed survey, which on a low-end panel device is
  // where a respondent typing a long answer loses keystrokes. Nothing on screen is lost by
  // coarsening it, because the label's own turnover is still in the signature: the words are
  // re-rendered on the exact second they change and on no other.
  const readout = show ? (final ? `final:${remaining}` : `calm:${formatRemaining(remaining)}`) : '';
  const nudge = nudgeVisible ? (isNudgeUrgent(remaining) ? 'amber' : 'calm') : '';
  return `${readout}|${nudge}`;
};

/**
 * Whether a hard stop is close enough that the tick has to keep running regardless of the screen.
 *
 * The stop itself rides on that tick: without it the deadline would only be noticed on the next
 * heartbeat, up to a minute late, and the retry after a refusal would never wake at all.
 */
export const hardStopIsClose = (remaining: number | null, mode: SurveyTimerMode): boolean =>
  mode === 'hard_stop' && remaining !== null && remaining <= FINAL_COUNTDOWN_SECONDS;

/**
 * Whether that forced tick still has anything to carry.
 *
 * Once the ask has been refused for a reason this session can never recover from, no tick will
 * ever land the stop — `remaining` sits at 0, the latch stays taken and nothing navigates — so
 * forcing one re-rendered the ~4,900-line respondent runtime once a second for the life of the
 * page, with the respondent stranded on a red 0:00 that no longer means anything.
 */
export const hardStopTickNeeded = (
  remaining: number | null,
  mode: SurveyTimerMode,
  expiryUnreachable: boolean,
): boolean => !expiryUnreachable && hardStopIsClose(remaining, mode);

/**
 * Whether to ask the server to end the session.
 *
 * Only in hard-stop mode, and only once. The server re-derives the deadline and refuses if it
 * disagrees, so this is a prompt rather than an instruction — a client with a skewed clock
 * cannot end anyone's survey early.
 *
 * `serverSaysExpired` is the other way in, and it does NOT wait for our own clock to agree — nor
 * for our own CONFIG to. Every write path drops post-buzzer answers while still answering
 * `status: success`, flagging it only with `survey_timer_expired`, and only a hard stop ever sets
 * that flag. So once one comes back the server's deadline is a fact, and whatever this page loaded
 * with is at best an estimate of it and at worst absent altogether: the case the flag exists for is
 * a hard stop switched on under a tab that loaded the survey UNTIMED, which never called
 * /timer/start and whose deadline the server derives from the session's own created_at. Requiring
 * `mode === 'hard_stop'` here put that case behind a config that is null in exactly that case, and
 * a `guidance` reading behind one that is stale by definition — leaving the respondent on a "time
 * is up" notice with nothing able to end their session and no panel redirect at the other end.
 *
 * Which is why `mode` is nullable rather than merely defaulted: an absent config is a real state
 * this decision has to be able to see, not a missing argument to fill in.
 *
 * `unreachable` is the OTHER latch, and it is a different question from `alreadyRequested`: that
 * one says an ask is out and has not come back, this one says no ask ever will. They are set and
 * released independently, so consulting only the first left a gap between them — the stall watchdog
 * gives `alreadyRequested` back at fifteen seconds so the ask can be retried, and the same request
 * rejecting 404 at twenty sets only `unreachable`. The retry that watchdog had already armed then
 * went ahead against an endpoint the hook had that second concluded can never answer, and if the
 * next reply is anything short of terminal it goes on doing so every thirty seconds for the life of
 * the page — on the unauthenticated respondent path, which is the loop `unreachable` exists to end.
 * `hardStopTickNeeded` had been reading it all along; this decision, which is the one that actually
 * makes the request, had not.
 */
export const shouldRequestExpiry = (
  remaining: number | null,
  mode: SurveyTimerMode | null,
  alreadyRequested: boolean,
  serverSaysExpired: boolean,
  unreachable: boolean,
): boolean =>
  !alreadyRequested &&
  !unreachable &&
  (serverSaysExpired || (mode === 'hard_stop' && remaining !== null && remaining <= 0));

/**
 * Whether a write the server answered `success` actually recorded anything.
 *
 * `process_response`, `submit_followup` and `moderator_submit_answer` all report success past the
 * hard stop and write NOTHING — deliberately, so the client stops retrying rather than looping on
 * an error. The whole difference is this flag, and a client that ignores it advances to the next
 * question and lets the respondent answer into a void; the backend's own comment names the case.
 */
export const isTimerExpiredWrite = (result: unknown): boolean =>
  Boolean((result as { survey_timer_expired?: unknown } | null | undefined)?.survey_timer_expired);

/**
 * The refusals from /timer/expire that this session can never recover from.
 *
 * The endpoint's own contract, all four of it: `survey_not_timed` and `survey_not_hard_stop` mean
 * the researcher's clock does not end this survey, `session_not_in_progress` means there is no
 * live session left to end, and `timer_not_started` means there is no deadline to be past —
 * "polling cannot make a deadline appear, only POST /timer/start can", which this hook has already
 * stopped doing by the time it is asking to expire. Retrying any of them is polling for ever.
 *
 * `timer_not_started` was left out when this list was written, on the grounds that its one
 * reachable cause (a session row with no readable `created_at`) is vanishingly rare. But the
 * default is RETRY, so leaving it out is not a no-op: it leaves `expiryUnreachable` false, which
 * pins the forced 1Hz hard-stop tick on for the life of the page — a re-render of the ~4,900-line
 * respondent runtime every second, plus a POST every thirty — which is precisely what that latch
 * exists to kill.
 */
export const TERMINAL_EXPIRY_REASONS = [
  'survey_not_timed',
  'survey_not_hard_stop',
  'session_not_in_progress',
  'timer_not_started',
] as const;

/**
 * The refusals from /timer/start that mean asking again can never help.
 *
 * That endpoint answers HTTP 200 with `{timer: null, reason}` — a refusal, not a dropped request —
 * and the two it can send are both permanent for this session: `session_not_in_progress` (the
 * 15-minute abandonment sweep, or screening, has already ended it) and `survey_not_timed` (the
 * researcher cleared the clock). Retrying either is a POST a minute, for ever, on the
 * unauthenticated respondent path.
 *
 * Same shape and the same default as the expiry list, for the same reason: only a reason we
 * RECOGNISE stops the asking, so a transient refusal invented by a proxy — or one the backend adds
 * later — leaves the survey retrying rather than silently unclocked. A start that fails to land is
 * a hard stop with no deadline at either end.
 */
export const TERMINAL_START_REASONS = ['session_not_in_progress', 'survey_not_timed'] as const;

/**
 * Whether a refusal cancels the hard stop for good.
 *
 * The default is deliberately the RETRYING one. Treating every reason but `not_due` as final
 * meant any reason the backend added later — `survey_not_hard_stop` and `session_not_in_progress`
 * both arrived after this shipped — silently disabled the deadline for the rest of the session,
 * and a transient one from a proxy did the same. A deadline may fail closed; it must never fail
 * open, so only what is recognised here is allowed to stop the asking.
 */
export const isTerminalExpiryRefusal = (reason: string | undefined): boolean =>
  typeof reason === 'string' && (TERMINAL_EXPIRY_REASONS as readonly string[]).includes(reason);

/**
 * Whether a refused start is worth asking again.
 *
 * The default is the retrying one, exactly as above: an unrecognised reason, or none at all, is
 * treated as a request that did not land. Only a refusal we know to be permanent stands the retry
 * down — because the alternative to retrying is a survey with no clock, and in hard-stop mode that
 * is no deadline at EITHER end.
 */
export const isTerminalStartRefusal = (reason: string | undefined): boolean =>
  typeof reason === 'string' && (TERMINAL_START_REASONS as readonly string[]).includes(reason);

/**
 * The 4xx statuses that mean "not yet" rather than "never".
 *
 * A timeout and a rate limit describe the REQUEST rather than the thing it asked for, so those are
 * two a retry can still get past.
 *
 * 400 is the third, and it is not a generic bad-request allowance: it is the ONE status
 * /timer/expire raises itself, and it raises it on a failure it means the client to ask again
 * after. The completion and the timeout tag are written in a single transaction, and a
 * `complete_survey_session` that reports an error inside it raises 400 with nothing committed —
 * the endpoint's own comment reads "this endpoint is retried by contract every 30 seconds, so
 * undoing a half-finished attempt costs nothing and leaves the timeout still due". Classed
 * permanent, one transient 400 latched `expiryUnreachable` for the life of the page, and the
 * respondent either took a completion page with no `redirect_url` while their session was still
 * `in_progress` — a panel completion lost — or sat on a frozen red 0:00 with the session never
 * ending and every further answer silently discarded.
 *
 * Shared with /timer/start, which cannot answer 400 at all: it raises nothing but a 404 for a
 * session it cannot find, and every refusal it has is an HTTP 200. So a 400 arriving there was
 * invented in front of the app — a proxy, a captive portal — and retrying it is the same
 * fail-closed default this module takes everywhere else.
 *
 * NOT widened to "everything but 404". 403 is reachable on both endpoints and is as permanent as
 * a 404: the marketplace account guard rejects every non-allowlisted path, /api/surveys/*
 * included, whenever the browser carries a marketplace session cookie — and that cookie does not
 * change mid-session, so retrying it is the POST-every-thirty-seconds loop this whole
 * classification exists to prevent.
 */
export const RETRYABLE_CLIENT_ERROR_STATUSES = [400, 408, 429] as const;

/**
 * Whether an HTTP failure means asking again can never help.
 *
 * The refusal lists above cover the endpoints' HTTP-200 contract; this is its missing half. Both
 * timer requests are retried on ANY rejection, for ever — right for a dropped packet, wrong for a
 * 404, which is what both endpoints answer for a session that has been deleted or purged. That is
 * a POST every thirty seconds to /timer/expire, and one a minute to /timer/start at the backoff
 * ceiling, for the life of the page, on the unauthenticated respondent path — the exact hazard
 * this module's own comments name for the refusal path and then never covered here.
 *
 * Only a 4xx counts, and only one carrying a status at all: a rejection with nothing on it is a
 * dropped request, and a 5xx is a server having a bad minute. Same default as everywhere else in
 * this module — what is not RECOGNISED as permanent keeps being asked for, because a deadline may
 * fail closed and must never fail open.
 *
 * Which is why the RETRYABLE side is the enumerated one here, the opposite way round from the
 * HTTP-200 refusal lists above, and deliberately so. A refusal reason is a word the backend chose
 * and can add to at any time, so an unrecognised one is most likely something new a retry can
 * still get past. A status is not: between them these two endpoints raise exactly 400 and 404,
 * and the middleware in front of them adds 403, so a 4xx outside that set did not come from this
 * contract at all — and on the unauthenticated respondent path, a permanent failure asked for
 * every thirty seconds is the more expensive of the two mistakes.
 */
export const isUnrecoverableRequestFailure = (error: unknown): boolean => {
  const status = (error as { status?: unknown } | null | undefined)?.status;
  if (typeof status !== 'number') return false;
  if ((RETRYABLE_CLIENT_ERROR_STATUSES as readonly number[]).includes(status)) return false;
  return status >= 400 && status < 500;
};

/**
 * How many times running the ask to end the session may fail before it is given up on.
 *
 * The classification above stands down on a failure that names itself permanent. This bounds the
 * one that never does — a 500, or a request that keeps stalling — which otherwise posts every
 * thirty seconds for as long as the tab is open. Ten is five minutes of trying at
 * EXPIRY_RETRY_INTERVAL_MS: far longer than any outage a respondent sits through, and ten requests
 * rather than an unbounded stream once the server is simply not coming back.
 *
 * Counts CONSECUTIVE failures only — anything that comes home, granted or refused, proves the
 * channel works and puts the count back to zero, so a long session on a flaky connection is never
 * quietly disarmed by failures it already recovered from.
 */
export const MAX_EXPIRY_REQUEST_FAILURES = 10;

/**
 * The config as the SERVER now has it, folded onto the copy the page loaded with.
 *
 * `SurveyTimerView` carries `mode`, `visible` and `duration_seconds` on every reading — shipped
 * deliberately, so "a survey switched to hard stop mid-session reaches the respondent's browser
 * without a second endpoint". Keeping only `remaining_seconds` and `paused` froze the config at
 * whatever `getPublicSurvey` returned at page load, and it breaks both ways: a guidance tab never
 * starts enforcing a hard stop the researcher switched on (the server just begins dropping its
 * writes, with no notice), and a hard-stop tab switched to guidance keeps asking to expire, is
 * told `survey_not_hard_stop`, latches, and pins a red 0:00 to the rest of the survey.
 *
 * Returns `base` ITSELF when nothing usable or nothing different arrived, so the caller can put
 * this straight into `setState` and get React's bail-out: a reading a minute for a survey nobody
 * is editing must not re-render the respondent runtime, and a reading that carries no config at
 * all — a malformed or reshaped envelope — must not reset the config the page loaded with.
 */
export const configWithServerView = (
  base: SurveyTimerConfig | null,
  view:
    | {
        mode?: unknown;
        visible?: unknown;
        duration_seconds?: unknown;
      }
    | null
    | undefined,
): SurveyTimerConfig | null => {
  if (!base || !view) return base;
  const mode = view.mode === 'hard_stop' || view.mode === 'guidance' ? view.mode : base.mode;
  const visible = typeof view.visible === 'boolean' ? view.visible : base.visible;
  const duration =
    typeof view.duration_seconds === 'number' && view.duration_seconds > 0
      ? view.duration_seconds
      : base.duration_seconds;
  if (mode === base.mode && visible === base.visible && duration === base.duration_seconds) {
    return base;
  }
  return { ...base, mode, visible, duration_seconds: duration };
};

/** Everything the respondent runtime can be showing INSTEAD of a nudge. */
export interface NudgeSlotState {
  /** Inside a live rapid-fire block, where the message is deliberately nulled at the render. */
  rapidFireActive: boolean;
  /** Mid conditional-skip: a bare spinner stands where the whole page was. */
  skipping: boolean;
  /** A fraud gate — CAPTCHA or geo — has replaced the whole survey. */
  gated: boolean;
  /** There is a question on screen to hang a nudge on at all. */
  hasQuestion: boolean;
}

/**
 * Whether there is anywhere to put a nudge right now.
 *
 * A mark raised with no slot is recorded as shown and then auto-dismissed unseen, which is the
 * warning being destroyed rather than delayed — a chain of conditionally-skipped questions
 * spanning the five-minute mark ate it outright. So every state that leaves the live region off
 * the screen has to be listed here.
 *
 * The list is now SHORTER than it was, and it is the render that shortened it. This predicate was
 * written when `TimerNudge` sat inside the question body, so everything that replaced that body —
 * the AI spinner, the conditional-skip check, the "thinking" delay, the rapid-fire intro — took
 * the region with it. The live region has since been hoisted ABOVE all four, and a predicate that
 * still claims there is no slot during them holds a mark that the region is on screen and able to
 * show: the same warning lost, through the door the fix for it opened.
 *
 * What remains are the three that genuinely have nowhere: `skipping` and `gated` early-return the
 * entire page, and `rapidFireActive` renders the region with its message explicitly nulled —
 * that section owns the screen and its whole point is undivided attention.
 *
 * A pure function rather than an expression at the call site so each term is independently
 * testable — a term that disagrees with the render is exactly the defect this keeps re-having.
 */
export const nudgeSlotAvailable = (state: NudgeSlotState): boolean =>
  !state.rapidFireActive && !state.skipping && !state.gated && state.hasQuestion;

/**
 * How a hard stop tells the completion page that this session ended on the buzzer.
 *
 * The flag was already being written into the URL and read absolutely nowhere, so a respondent cut
 * off at question 12 of 40 landed on a green tick and "Survey Complete" — congratulated on
 * finishing something they were stopped part-way through, with no mention that time had run out.
 * That is the silent stop this whole design exists to prevent, moved one screen later.
 *
 * The pair is exported together, and built and read through the two helpers below, because a
 * producer and a consumer of the same query flag drifting apart is exactly how it came to be
 * written by one side and read by neither.
 */
export const TIMED_OUT_PARAM = 'timed_out';
export const TIMED_OUT_FLAG = '1';

/**
 * And how it tells that page the stop was never PAID for.
 *
 * The timed-out page asserts "your answers have been saved" — a claim about the database — and
 * there are two ways to reach it with that claim false. /timer/expire can grant the stop and
 * answer `credited: false`, which retires the session unpaid with `redirect_url: null` (a hard
 * stop is anchored on the session's own `created_at`, so a short one runs out while the
 * respondent is still on welcome or consent, and a session that recorded nothing is not a
 * completion). Or the ask can die outright — a 404, or a run of failures long enough to bound it
 * — leaving the session `in_progress` for the abandonment sweep to take.
 *
 * Neither carries a panel redirect, so a panel respondent is not credited; being told their
 * answers are safe is the one thing that stops them asking about it.
 *
 * A NEGATIVE flag, written only when the completion is not credited, so the URL a credited stop
 * produces is byte-for-byte the one it produced before this existed.
 */
export const CREDITED_PARAM = 'credited';
export const NOT_CREDITED_FLAG = '0';

/**
 * Whether a hard-stop envelope says the session actually became a completion.
 *
 * `credited === true` and nothing else. The claim it gates is about a row in the database, and
 * the absence of the field is not evidence for it: the synthetic envelope this hook builds for an
 * ask that could never be made carries no completion at all, and neither does a terminal refusal.
 * BE #671 puts `credited` on every granted reply, and this FE ships before it — so the only
 * backend that can ever answer this endpoint is one that carries the field.
 */
export const isCreditedCompletion = (result: { credited?: unknown } | null | undefined): boolean =>
  result?.credited === true;

/** Where a hard stop sends a respondent when the server returned no panel redirect. */
export const timedOutCompletionPath = (sessionId: number, credited: boolean): string =>
  `/survey-complete/${sessionId}?${TIMED_OUT_PARAM}=${TIMED_OUT_FLAG}` +
  (credited ? '' : `&${CREDITED_PARAM}=${NOT_CREDITED_FLAG}`);

/** Whether the completion page was reached by the buzzer rather than by finishing. */
export const isTimedOutCompletion = (params: URLSearchParams): boolean =>
  params.get(TIMED_OUT_PARAM) === TIMED_OUT_FLAG;

/** Whether that buzzer left the session short of a real, credited completion. */
export const isUncreditedCompletion = (params: URLSearchParams): boolean =>
  params.get(CREDITED_PARAM) === NOT_CREDITED_FLAG;

/**
 * What the hook tells the caller about the ask it is ABOUT to make.
 *
 * `beforeHardStop` is the caller's last chance to write anything belonging to this session, and
 * it runs before EVERY ask — including the first, which is refused by construction: the client
 * asks the moment its own interpolation hits zero, and `should_hard_stop` grants only once the
 * deadline is past by MORE than `HARD_STOP_GRACE_SECONDS` (the backend's own comment names the
 * frontend as the caller that "requests expiry at exactly that moment"). A write that is only
 * correct if the session is actually about to end therefore has to know which ask this is.
 */
export interface HardStopAsk {
  /**
   * Whether the server can actually grant THIS ask.
   *
   * False for the first ask of a hard stop, and true from then on. A retry is timed off the
   * refusal's own reading plus the grace the server insists on plus a second
   * (`expiryRetryDelayMs`), so it lands past the boundary rather than on it; a retry after a
   * failure or a stall is further out still. An ask driven by `serverSaysExpired` is grantable
   * immediately — the server has already begun discarding this respondent's writes, which is
   * only true past that same boundary.
   */
  grantable: boolean;
}
